import {
  Badge,
  Button,
  Group,
  Paper,
  PasswordInput,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  changePasswordSchema,
  updateProfileSchema,
  type ChangePasswordInput,
  type UpdateProfileInput,
} from '@creativitas/shared';
import { toUserMessage } from '@/lib/api';
import type { ReactNode } from 'react';
import { IconMailCheck } from '@tabler/icons-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { CardGridSkeleton, ErrorState } from '@/components/ui/StateViews';
import { UserChip } from '@/components/domain/ParticipantChip';
import {
  ManualGrantsTile,
  OwnedGamesTile,
  SubscriptionTile,
  TrialTile,
} from '@/features/access/AccessStateCards';
import { changePassword, updateProfile } from '@/features/auth/api';
import { SESSION_QUERY_KEY, useSession } from '@/features/auth/useSession';
import { RecurringCard } from '@/features/payments/RecurringCard';
import { ConsentsCard, DeleteAccountCard } from './PrivacySection';

const formatDateTime = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

/** Label/value row that stacks under its label on narrow screens (§57). */
function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Group gap="sm" align="flex-start" wrap="wrap">
      <Text size="sm" c="var(--text-muted)" w={{ base: '100%', xs: 200 }}>
        {label}
      </Text>
      <div style={{ minWidth: 0, flex: 1 }}>{children}</div>
    </Group>
  );
}

export function ProfilePage() {
  const { user, access, isLoading, isError, refetch } = useSession();

  if (isLoading) return <CardGridSkeleton count={2} />;
  if (isError || !user || !access) return <ErrorState onRetry={() => void refetch()} />;

  return (
    <>
      {/* Logging out lives in the user menu, next to the account it ends. */}
      <PageHeader
        title="Профиль"
        description="Ваши данные и текущий доступ к материалам платформы."
      />

      <Stack gap="xl">
        <Paper withBorder p="lg" radius="lg" style={{ borderColor: 'var(--border-subtle)' }}>
          <Stack gap="md">
            <UserChip name={user.displayName} secondary={user.email} />

            {/*
              A definition list rather than a fixed-width table: at 375px a
              220px label column forced the page into horizontal scroll (§57).
            */}
            <Stack gap="sm">
              <DetailRow label="Роль">
                <Badge variant="light" color={user.role === 'admin' ? 'gold' : 'teal'}>
                  {user.role === 'admin' ? 'Администратор' : 'Пользователь'}
                </Badge>
              </DetailRow>

              <DetailRow label="Почта">
                <Group gap="xs" wrap="wrap">
                  <Text size="sm" style={{ wordBreak: 'break-all' }}>
                    {user.email}
                  </Text>
                  {user.emailVerified ? (
                    <Badge
                      size="xs"
                      variant="light"
                      color="teal"
                      leftSection={<IconMailCheck size={11} />}
                    >
                      подтверждена
                    </Badge>
                  ) : null}
                </Group>
              </DetailRow>

              <DetailRow label="Регистрация">
                <Text size="sm">{formatDateTime(user.createdAt)}</Text>
              </DetailRow>

              <DetailRow label="Последний вход">
                <Text size="sm">{formatDateTime(user.lastLoginAt)}</Text>
              </DetailRow>
            </Stack>
          </Stack>
        </Paper>

        <Stack gap="sm">
          <Title order={3}>Настройки аккаунта</Title>
          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
            <DisplayNameForm currentName={user.displayName} />
            <PasswordForm />
          </SimpleGrid>
        </Stack>

        <Stack gap="sm">
          <Title order={3}>Доступ</Title>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
            <TrialTile trial={access.trial} />
            <SubscriptionTile subscription={access.subscription} />
            <OwnedGamesTile games={access.ownedGames} />
            <ManualGrantsTile grants={access.manualGrants} />
          </SimpleGrid>
          <RecurringCard />
          <Text size="sm" c="var(--text-muted)">
            Тариф и владение игрой может выдать и администратор — например, если оплата прошла не через сайт.
          </Text>
        </Stack>

        <Stack gap="sm">
          <Title order={3}>Персональные данные</Title>
          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
            <ConsentsCard />
            <DeleteAccountCard access={access} isAdmin={user.role === 'admin'} />
          </SimpleGrid>
        </Stack>
      </Stack>
    </>
  );
}

/** Renaming the account. The email is fixed — it is the login (§14). */
function DisplayNameForm({ currentName }: { currentName: string }) {
  const queryClient = useQueryClient();
  const form = useForm<UpdateProfileInput>({
    initialValues: { displayName: currentName },
    validate: zodResolver(updateProfileSchema),
    validateInputOnBlur: true,
  });

  const save = useMutation({
    mutationFn: updateProfile,
    onSuccess: (data) => {
      queryClient.setQueryData(SESSION_QUERY_KEY, data.user ? data : null);
      notifications.show({ color: 'teal', message: 'Имя обновлено' });
      form.resetDirty();
    },
    onError: (error) => notifications.show({ color: 'red', message: toUserMessage(error) }),
  });

  return (
    <Paper withBorder p="lg" radius="lg" style={{ borderColor: 'var(--border-subtle)' }}>
      <form onSubmit={form.onSubmit((values) => save.mutate(values))} noValidate>
        <Stack gap="md">
          <div>
            <Title order={4}>Отображаемое имя</Title>
            <Text size="sm" c="var(--text-muted)">
              Так вас видят участники ваших сессий.
            </Text>
          </div>
          <TextInput
            label="Имя"
            autoComplete="name"
            {...form.getInputProps('displayName')}
          />
          <Group justify="flex-end">
            <Button
              type="submit"
              variant="light"
              loading={save.isPending}
              disabled={!form.isDirty()}
            >
              Сохранить
            </Button>
          </Group>
        </Stack>
      </form>
    </Paper>
  );
}

/**
 * Changing the password. The server ends every other session and re-issues a
 * cookie for this one, so the person is not thrown out of the tab they are in.
 */
function PasswordForm() {
  const queryClient = useQueryClient();
  const form = useForm<ChangePasswordInput>({
    initialValues: { currentPassword: '', newPassword: '' },
    validate: zodResolver(changePasswordSchema),
    validateInputOnBlur: true,
  });

  const save = useMutation({
    mutationFn: changePassword,
    onSuccess: (data) => {
      queryClient.setQueryData(SESSION_QUERY_KEY, data.user ? data : null);
      notifications.show({
        color: 'teal',
        message: 'Пароль изменён. Входы на других устройствах закрыты.',
      });
      form.reset();
    },
    onError: (error) => notifications.show({ color: 'red', message: toUserMessage(error) }),
  });

  return (
    <Paper withBorder p="lg" radius="lg" style={{ borderColor: 'var(--border-subtle)' }}>
      <form onSubmit={form.onSubmit((values) => save.mutate(values))} noValidate>
        <Stack gap="md">
          <div>
            <Title order={4}>Пароль</Title>
            <Text size="sm" c="var(--text-muted)">
              После смены входы на других устройствах закроются.
            </Text>
          </div>
          <PasswordInput
            label="Текущий пароль"
            autoComplete="current-password"
            {...form.getInputProps('currentPassword')}
          />
          <PasswordInput
            label="Новый пароль"
            autoComplete="new-password"
            {...form.getInputProps('newPassword')}
          />
          <Group justify="flex-end">
            <Button type="submit" variant="light" loading={save.isPending}>
              Сменить пароль
            </Button>
          </Group>
        </Stack>
      </form>
    </Paper>
  );
}
